import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface UploadBadgeProps {
    count: number
    reduced: boolean
}

interface Roll {
    current: number
    previous: number | null
    key: number
}

// The "Uploaded" pill. A new count rolls up from below while the old one rolls out the top.
export const UploadBadge: FC<UploadBadgeProps> = ({ count, reduced }): ReactNode => {
    const pillRef = useRef<HTMLDivElement>(null)
    const [roll, setRoll] = useState<Roll>({ current: count, previous: null, key: 0 })

    useEffect(() => {
        setRoll(state => state.current === count ? state : { current: count, previous: state.current, key: state.key + 1 })
    }, [count])

    useEffect(() => {
        if (roll.key === 0) return

        if (!reduced) {
            pillRef.current?.animate(
                [{ transform: 'scale(1)' }, { transform: 'scale(1.04)', offset: 0.45 }, { transform: 'scale(1)' }],
                { duration: 250, easing: 'ease-out' }
            )
        }

        const timer = window.setTimeout(() => setRoll(state => ({ ...state, previous: null })), 260)

        return () => window.clearTimeout(timer)
    }, [roll.key])

    const digits = String(roll.current).length

    return (
        <div ref={pillRef} className='mt-[2px] flex h-[26px] shrink-0 items-center gap-1.5 rounded-full bg-pill px-3'>
            <span className='text-[length:max(11px,calc(8.5px/var(--s,1)))] leading-none text-graphite'>
                Uploaded
            </span>

            <span
                className='relative block h-[1.3em] overflow-hidden text-[length:max(11px,calc(8.5px/var(--s,1)))] font-semibold leading-[1.3] tabular-nums text-ink transition-[width] duration-200 ease-out'
                style={{ width: `${digits + 0.15}ch` }}
            >
                <span className='sr-only'>
                    {roll.current}
                </span>

                {roll.previous !== null && (
                    <span key={`out-${roll.key}`} aria-hidden='true' className='nbn-roll-out absolute inset-0'>
                        {roll.previous}
                    </span>
                )}

                <span key={`in-${roll.key}`} aria-hidden='true' className={cn('absolute inset-0', roll.key > 0 && 'nbn-roll-in')}>
                    {roll.current}
                </span>
            </span>
        </div>
    )
}
